import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger, useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  ModalIsland,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import {
  certsExportApplyFilter,
  performCertsExport,
  setCertsExportIsLoading,
} from '../../../redux/certdetails/action';
import { getMessageKey } from '../../../utils/utils';

import SelectAndOrganizeYourData from './SelectAndOrganizeYourData';
import WhatToExport from './WhatToExport';

import './CertsExport.css';

function CertsExportModal({ isOpen, setIsOpen }) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled } = useDetectScroll(
    null,
    sectionRef
  );
  const {
    certsExport: {
      columnsToExport,
      applyFilter,
      certsExportIsLoading,
      certsExportStatus,
    } = {},
    global: { certKey } = {},
    core: { searchText } = {},
  } = useSelector((state) => state?.certifications?.details || {});

  const { messages } = useSelector((state) => state?.user);

  const handleClose = () => {
    setIsOpen(false);

    dispatch(setCertsExportIsLoading(false));
    dispatch(certsExportApplyFilter(false));
  };

  const handleExportClick = async () => {
    try {
      const result = await dispatch(
        performCertsExport(certKey, columnsToExport, applyFilter, searchText)
      );

      if (result.success) {
        handleClose();
      } else {
        Logger.error('Export API call failed');
      }
    } catch (error) {
      Logger.error('Export API call error:', error);
    }
  };

  const isExportAPIFailed = certsExportStatus?.exportStatus === 'FAILED';

  const certsExportModalBody = () => (
    <>
      <InlineMessage
        kind='background'
        text={
          messages['EXPORT_ALERT_FAILED_DESCRIPION.ATTESTATIONS'] ||
          localize(
            intl,
            getMessageKey('EXPORT_ALERT_FAILED_DESCRIPION.ATTESTATIONS')
          )
        }
        colorTheme='critical'
        size='medium'
        isVisible={isExportAPIFailed}
        leftIcon={<Icon kind='AlertCriticalFilled' />}
        className='CertsListExportErrorMessage'
      />
      <WhatToExport />
      <SelectAndOrganizeYourData />
    </>
  );

  const certsExportFooterActionBar = () => (
    <Box className='CertsExportModalFooter'>
      <div className='CertsExportModalFooter-actionButtons'>
        <Button
          kind='outlined'
          size='medium'
          onClick={() => {
            setIsOpen(false);
            dispatch(certsExportApplyFilter(false));
          }}>
          {messages.CERTS_CANCEL ||
            localize(intl, getMessageKey('CERTS_CANCEL'))}
        </Button>
        <div className='CertsExportModalFooter-actionButtons--primary'>
          <Button
            kind='filled'
            size='medium'
            onClick={handleExportClick}
            isLoading={certsExportIsLoading}
            loaderText='Loading'>
            {messages.CERTS_EXPORT ||
              localize(intl, getMessageKey('CERTS_EXPORT'))}
          </Button>
        </div>
      </div>
    </Box>
  );

  const title =
    messages.CERTS_EXPORT_TITLE ||
    localize(intl, getMessageKey('CERTS_EXPORT_TITLE'));
  const subtitle =
    messages.CERTS_EXPORT_SUBTITLE ||
    localize(intl, getMessageKey('CERTS_EXPORT_SUBTITLE'));

  return (
    <ModalIsland
      onClose={handleClose}
      isOpen={isOpen}
      className='CertsExportModal'
      title={title}
      subtitle={subtitle}
      headerIcon={<Icon kind='Export' size='large' />}
      contentBody={certsExportModalBody()}
      sectionRef={sectionRef}
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          {certsExportFooterActionBar()}
        </FooterActionBar>
      }
    />
  );
}

CertsExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default CertsExportModal;
